import React, { useEffect, useState } from 'react';
import { Navbar } from '../App';
import { useLocation } from 'react-router-dom';
import contentPT from '../data/content.pt.json';
import contentEN from '../data/content.en.json';
import projectsPT from '../data/projects.pt.json';
import projectsEN from '../data/projects.en.json';

// YouTube API Key - configure no arquivo .env
const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

export default function Projects() {
  const lang = 'en';
  const location = useLocation();
  const [colCount, setColCount] = useState(4); // 4 on desktop, 2 on mobile
  const content = contentEN;
  const baseData = projectsEN.projects;
  const [data, setData] = useState(baseData);
  const [modalId, setModalId] = useState(null);
  const modalOpen = Boolean(modalId);
  const [scrolled, setScrolled] = useState(false);

  // Fetch video details from YouTube API
  useEffect(() => {
    const fetchVideoDetails = async () => {
      const videoIds = baseData.map(project => project.id).filter(Boolean);
      
      console.log('=== Magic Fish - Carregamento de Projetos ===');
      console.log(`Total de projetos: ${baseData.length}`);
      console.log(`IDs únicos: ${[...new Set(videoIds)].length}`);
      console.log(`API Key configurada: ${YOUTUBE_API_KEY ? 'Sim' : 'Não'}`);
      
      if (videoIds.length === 0 || !YOUTUBE_API_KEY || YOUTUBE_API_KEY === 'YOUR_YOUTUBE_API_KEY') {
        console.log('⚠️ YouTube API key não configurada. Usando dados estáticos dos arquivos JSON.');
        console.log('Para habilitar títulos dinâmicos, configure REACT_APP_YOUTUBE_API_KEY no arquivo .env');
        return;
      }

      console.log('🔄 Buscando informações dos vídeos na API do YouTube...');
      
      try {
        const uniqueIds = [...new Set(videoIds)];
        const idsString = uniqueIds.join(',');
        const apiUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${idsString}&key=${YOUTUBE_API_KEY}`;
        
        console.log(`📡 Fazendo requisição para: ${uniqueIds.length} vídeos`);
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          console.error('❌ Erro ao buscar detalhes dos vídeos:', response.statusText);
          console.error(`Status: ${response.status}`);
          return;
        }

        const result = await response.json();
        const details = {};
        
        console.log(`✅ Resposta recebida: ${result.items?.length || 0} vídeos encontrados`);
        
        result.items?.forEach(item => {
          details[item.id] = {
            title: item.snippet.title,
            author: item.snippet.channelTitle,
            description: item.snippet.description
          };
          console.log(`  📹 ${item.id}: "${item.snippet.title}" - ${item.snippet.channelTitle}`);
        });

        // Atualiza os dados dos projetos com informações da API
        const updatedData = baseData.map(project => {
          if (details[project.id]) {
            return {
              ...project,
              title: details[project.id].title,
              subtitle: details[project.id].author
            };
          }
          return project;
        });

        setData(updatedData);
        console.log(`✨ ${Object.keys(details).length} vídeos carregados com sucesso!`);
        console.log('==========================================');
      } catch (error) {
        console.error('❌ Erro ao buscar detalhes dos vídeos:', error);
        console.error('Stack:', error.stack);
      }
    };

    fetchVideoDetails();
  }, [baseData]);

  useEffect(() => {
    const calcCols = () => {
      const w = window.innerWidth;
      setColCount(w <= 860 ? 2 : 4);
    };
    calcCols();
    window.addEventListener('resize', calcCols);
    return () => window.removeEventListener('resize', calcCols);
  }, []);

  // Toggle navbar background when not at the very top
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Open modal automatically if video parameter is in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const videoId = params.get('video');
    if (videoId) {
      setModalId(videoId);
    }
  }, [location]);

  // Close on ESC and lock body scroll when modal open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setModalId(null);
    };
    if (modalOpen) {
      document.addEventListener('keydown', onKey);
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('modal-open');
    };
  }, [modalOpen]);

  // Distribute items across N columns, preserving order
  const columns = Array.from({ length: colCount }, () => []);
  data.forEach((item, idx) => {
    columns[idx % colCount].push(item);
  });

  const getMaxResThumb = (id) => id ? `https://i.ytimg.com/vi/${id}/maxresdefault.jpg` : '/placeholder.png';
  const getHQThumb = (id) => id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : '/placeholder.png';

  const renderCol = (items, colIndex) => {
    return items.map((item, idx) => {
      // Height pattern by column index: even -> [220, 480], odd -> [480, 220]
      const heights = colIndex % 2 === 0 ? [220, 480] : [480, 220];
      const h = heights[idx % 2];
      
      // Use custom thumbnail if available, otherwise use YouTube thumbnail
      const thumbnailSrc = item.thumbnail && item.thumbnail.trim() !== '' 
        ? item.thumbnail 
        : getMaxResThumb(item.id);
      
      return (
        <a
          key={`${colIndex}-${idx}`}
          href={`https://www.youtube.com/watch?v=${item.id}`}
          target="_blank"
          rel="noreferrer"
          className={`proj-card`}
          style={{ height: `${h}px` }}
          onClick={(e) => {
            e.preventDefault();
            setModalId(item.id);
          }}
        >
          <img
            src={thumbnailSrc}
            alt={item.title}
            onError={(e) => {
              const img = e.currentTarget;
              // Only fallback to YouTube HQ thumbnail if we're using YouTube thumbnails
              if (!item.thumbnail || item.thumbnail.trim() === '') {
                if (!img.dataset.fallback) {
                  img.dataset.fallback = '1';
                  img.src = getHQThumb(item.id);
                }
              }
            }}
          />
          <div className="proj-meta">
            <div className="proj-title">{item.title}</div>
            {item.subtitle ? (
              <div className="proj-sub">{item.subtitle}</div>
            ) : null}
          </div>
        </a>
      );
    });
  };

  return (
    <>
      <Navbar brand={content.brand} dark navColor="black" scrolled={scrolled} />
      <main className="page projects-page">
        <div className="projects-container">
          <div className="projects-grid">
            {columns.map((colItems, cIdx) => (
              <div className="projects-col" key={`col-${cIdx}`}>
                {renderCol(colItems, cIdx)}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Video Modal */}
      {modalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setModalId(null)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" aria-label="Close video" onClick={() => setModalId(null)}>
              ×
            </button>
            <div className="modal-video-wrap">
              <iframe
                title="Project Video"
                src={`https://www.youtube.com/embed/${modalId}?autoplay=1&rel=0&modestbranding=1&controls=1`}
                frameBorder="0"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
